/**
 * **A vida-base de uma nave, e o único sítio onde ela está escrita.**
 *
 * ## O defeito que este ficheiro existe para não repetir
 *
 * O número 100 vivia em `hunt/balance.ts` e o duelo tinha o seu, 1 000, herdado
 * da época em que o PvP normalizava o equipamento fora e todos os cascos eram
 * iguais. Enquanto o duelo lia uma constante própria, os dois podiam discordar
 * sem que nada os comparasse - e discordaram.
 *
 * O gate V28G.S ligou a vida máxima AUTORITATIVA da conta ao duelo e usou a
 * mesma constante de 1 000 como denominador da geometria. A consequência
 * apareceu na primeira sala aberta com contas reais: uma conta nova tem 100 de
 * vida, `100 / 1 000` dá **0,1x**, e o casco era desenhado com quatro píxeis de
 * diâmetro. As barras de vida, que não encolhem com ele, ficaram a flutuar
 * sobre nada.
 *
 * Nenhuma prova automática o apanhou porque **todas** usam o perfil por omissão,
 * e o perfil por omissão trazia exatamente 1 000 - ou seja, o único casco do
 * mundo que media `1x` era o que nenhum jogador tem.
 *
 * ## A decisão
 *
 * Há uma vida-base, ela vale 100, e ela é **neutra**: não é da caça nem do
 * duelo. A composição de vida máxima parte dela e a geometria do duelo divide
 * por ela, portanto uma conta nova mede exatamente `1x` nos dois sítios por
 * construção - e não por dois números calharem concordar.
 *
 *     escalaPvP = vidaMaximaMilli / SHIP_BASE_MAX_HEALTH_MILLI
 *
 * | vida máxima | escala |
 * | ---: | ---: |
 * | 100 | 1x |
 * | 101 | 1,01x |
 * | 160 | 1,6x |
 * | 1 000 | 10x |
 * | 10 000 | 100x |
 *
 * Linear, sem teto e sem piso. Cada ponto de vida acrescenta exatamente `0,01x`,
 * que é o que faz um ponto comprado ver-se.
 *
 * ## Porque vive em `hull/` e não em `hunt/` nem em `pvp/`
 *
 * Porque `hull/` já é o sítio neutro que os dois modos atravessam - a
 * composição da vida máxima, a escala do casco e a velocidade de movimento
 * estão todas aqui - e porque `pvp/` **não pode importar** `hunt/`: seria o modo
 * a depender do outro para saber o tamanho de uma nave.
 *
 * ## O que este número NÃO é
 *
 * Não é um teto, não é um piso e não é um valor por omissão de jogo. É a vida de
 * uma nave sem um único ponto alocado, sem artefacto, sem equipamento e sem
 * joia - e é por isso que serve as duas coisas ao mesmo tempo: é o começo da
 * composição, e é o `1x` da curva.
 *
 * Continua **provisório** como conteúdo, exatamente como era em
 * `HUNT_BALANCE.ship.baseHealth`. Movê-lo para aqui não o decidiu; deu-lhe um
 * dono.
 */

/** A vida-base de uma nave, em PONTOS. A fonte autoritativa e neutra. */
export const SHIP_BASE_MAX_HEALTH = 100;

/**
 * A mesma vida-base, em MILLI. Derivada, e nunca escrita à mão.
 *
 * `100 000`. É este o número por que a geometria do duelo divide, e é este o
 * teto que uma cadeira ainda vazia carrega antes de alguém se sentar nela.
 *
 * A unidade CANCELA na curva - `vidaMilli / referenciaMilli` é a mesma razão que
 * `vida / referencia` - portanto a tabela acima vale nas duas unidades.
 */
export const SHIP_BASE_MAX_HEALTH_MILLI = BigInt(SHIP_BASE_MAX_HEALTH) * 1_000n;
